import EventEmitter2 from 'eventemitter2';
import IAppLogger from './IAppLogger';
import IAppManager from './IAppManager';

const EMITTER_LISTENERS_NUMBER = 30;

class AppEvents {
  private readonly _emitter: EventEmitter2 = new EventEmitter2();
  private readonly _events: string[] = [];
  private _enabled: boolean = false;
  private _testing: boolean = false;
  private readonly _logger: IAppLogger;

  constructor(appManager: IAppManager) {
    this._logger = appManager.logger('app', 'info');
    this._emitter.setMaxListeners(EMITTER_LISTENERS_NUMBER);
  }

  public on<T>(event: string, listener: (arg: T) => void): void;
  public on(event: string, listener: (...args: any[]) => void): void {
    if (!this._testing && !this._events.includes(event)) {
      throw new Error(`Event ${event} is not registered`);
    }
    this._emitter.on(event, listener);
  }

  public onAny(
    listener: (event: string | string[], ...args: any[]) => void,
  ): void {
    this._emitter.onAny(listener);
  }

  public off(event: string, listener: (...args: any[]) => void): void {
    if (!this._testing && !this._events.includes(event)) {
      throw new Error(`Event ${event} is not registered`);
    }
    this._emitter.off(event, listener);
  }

  public emit(event: string): void;
  public emit<T>(event: string, arg: T): void;
  public emit(event: string, ...args: any[]): void {
    if (!this._enabled) {
      this._logger.warn(`Event ${event} emitted before enabled`);
      return;
    } else if (!this._testing && !this._events.includes(event)) {
      throw new Error(`Event ${event} is not registered`);
    }

    // emit event
    // make sure we don't bubble errors up to caller
    try {
      this._emitter.emit(event, ...args);
    } catch (err) {
      this._logger.error(
        `Event listener threw an exception for event '${event}'`,
        err,
      );
    }
  }

  public registerEvent(event: string): void {
    this._events.push(event);
  }

  public enable(testing: boolean = false): void {
    this._testing = testing;
    this._enabled = true;
  }

  /**
   * Listen for multiple events and invoke the callback once after all have fired (in any order).
   * Returns an unsubscribe function.
   */
  public onAll(
    events: readonly string[],
    callback: (...args: any[]) => void,
  ): () => void {
    // Validate event registration when not in testing mode
    if (!this._testing) {
      for (const evt of events) {
        if (!this._events.includes(evt)) {
          throw new Error(`Event ${evt} is not registered`);
        }
      }
    }

    const seen: boolean[] = new Array(events.length).fill(false);
    const results: unknown[] = new Array(events.length);
    const handlers: Array<(...args: unknown[]) => void> = [];

    const off = () => {
      events.forEach((evt, idx) => {
        const h = handlers[idx];
        if (h) this._emitter.off(evt, h as any);
      });
    };

    const tryFire = () => {
      if (seen.every(Boolean)) {
        off();
        const filtered = AppEvents.dropVoidRuntime(results, seen);
        callback(...filtered);
      }
    };

    events.forEach((evt, idx) => {
      const handler = (...args: unknown[]) => {
        if (seen[idx]) return;
        seen[idx] = true;
        results[idx] = args[0];
        tryFire();
      };
      handlers[idx] = handler;
      this.on(evt, handler as any);
    });

    return off;
  }

  // Utility type: drop void entries from tuples
  private static dropVoidRuntime(args: unknown[], seen: boolean[]) {
    return args.filter((_, idx) => seen[idx] && args[idx] !== undefined);
  }
}

export default AppEvents;
